import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Pencil, Users, Search, HandHelping, Send, Undo2, Check, Maximize2, Clock, Package, AlertTriangle, Building2 } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useGroups } from '../store/groups';
import { useSponsors } from '../store/sponsors';
import { useConsent } from '../store/consent';
import { useInventory } from '../store/inventory';
import { supabase } from '../lib/supabase';
import { Avatar, Badge, BottomSheet, Spinner, ImageLightbox, ImageTile, DueSoonBanner } from '../components/UI';
import { CategoryIcon } from '../lib/categoryIcons';
import { EditItemSheet } from '../components/EditItemSheet';
import { SponsorBanner } from '../components/SponsorBanner';

export const ToolsPage: React.FC<{ showSearch: boolean }> = ({ showSearch }) => {
  const { user, profile } = useAuth();
  const { categories, members, currentGroup, groups, loading: groupsLoading } = useGroups();
  const { sponsors, fetchActiveSponsors } = useSponsors();
  const { sponsorConsent } = useConsent();

  const {
    loanableItems, myLoans,
    fetchLoanableItems, fetchMyLoans, fetchMyLentOut,
    requestLoanItem, approveLoanItem, rejectLoanItem, returnItem,
  } = useInventory();

  const [catFilter, setCatFilter] = useState('Alle');
  const [memberFilter, setMemberFilter] = useState('');
  const [availableFilter, setAvailableFilter] = useState(false);
  const [newFilter, setNewFilter] = useState(false);
  const [showMembersSheet, setShowMembersSheet] = useState(false);
  const [search, setSearch] = useState('');
  const [requesting, setRequesting] = useState<string | null>(null);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());
  const [requestError, setRequestError] = useState<string | null>(null);
  const [loanDate, setLoanDate] = useState('');
  const [loanWeeks, setLoanWeeks] = useState<number | null>(2);
  const [showPicker, setShowPicker] = useState(false);
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const catBtnRef = useRef<HTMLDivElement>(null);
  const personBtnRef = useRef<HTMLDivElement>(null);
  const [catDropPos, setCatDropPos] = useState({ top: 0, left: 0 });
  const [personDropPos, setPersonDropPos] = useState({ top: 0, left: 0 });

  const [editId, setEditId] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [approveMessage, setApproveMessage] = useState('');
  const [returnMessage, setReturnMessage] = useState('');
  const [crossResults, setCrossResults] = useState<any[]>([]);
  const [crossLoading, setCrossLoading] = useState(false);

  const curGroup = currentGroup();
  const curGroupId = curGroup?.id;

  useEffect(() => {
    if (!user) return;
    fetchMyLoans(user.id);
    fetchMyLentOut(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (curGroupId) fetchLoanableItems(curGroupId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curGroupId]);

  useEffect(() => {
    fetchActiveSponsors(profile?.kommune, profile?.fylke);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.kommune, profile?.fylke]);

  useEffect(() => {
    if (!showSearch) { setSearch(''); setCrossResults([]); }
  }, [showSearch]);

  useEffect(() => {
    if (!showSearch || !search.trim()) { setCrossResults([]); return; }
    const timer = setTimeout(async () => {
      if (groups.length === 0) return;
      setCrossLoading(true);
      const groupIds = groups.map(g => g.id);
      const { data: igData } = await supabase
        .from('inventory_groups')
        .select('inventory_id, group_id')
        .in('group_id', groupIds);
      const inventoryIds = [...new Set((igData || []).map((r: any) => r.inventory_id))];
      if (!inventoryIds.length) { setCrossResults([]); setCrossLoading(false); return; }
      const q = search.trim();
      const { data: rawItems } = await supabase
        .from('inventory')
        .select('*')
        .eq('loanable', true)
        .in('id', inventoryIds)
        .or(`navn.ilike.%${q}%,beskrivelse.ilike.%${q}%`)
        .order('status', { ascending: true })
        .limit(50);
      if (!rawItems?.length) { setCrossResults([]); setCrossLoading(false); return; }
      const profileIds = [...new Set([
        ...rawItems.map((i: any) => i.user_id),
        ...rawItems.filter((i: any) => i.loaned_to_id).map((i: any) => i.loaned_to_id),
      ])];
      const { data: profiles } = await supabase.from('profiles').select('id, full_name, avatar_color').in('id', profileIds);
      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      const grpMap = new Map<string, string[]>();
      (igData || []).forEach((l: any) => {
        if (!grpMap.has(l.inventory_id)) grpMap.set(l.inventory_id, []);
        const g = groups.find(gr => gr.id === l.group_id);
        if (g) grpMap.get(l.inventory_id)!.push(g.name);
      });
      setCrossResults(rawItems.map((item: any) => ({
        ...item,
        owner: profileMap.get(item.user_id),
        loaned_to: item.loaned_to_id ? profileMap.get(item.loaned_to_id) : undefined,
        groupNames: grpMap.get(item.id) || [],
      })));
      setCrossLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showSearch, groups.length]);

  const derivedCats = [...new Set(
    loanableItems.flatMap(i => (i.filterCategories ?? []).map(c => c.name))
  )].sort();
  const catOptions = ['Alle', ...derivedCats];
  const activeMember = members.find(m => m.user_id === memberFilter);

  const isRecent = (createdAt: string) => Date.now() - new Date(createdAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  const hasNewItems = loanableItems.some(t => isRecent(t.created_at));

  useEffect(() => {
    if (!hasNewItems) setNewFilter(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNewItems]);

  const matchesCatFilter = (t: any) =>
    catFilter === 'Alle' || (t.filterCategories ?? []).some((c: any) => c.name === catFilter);

  const filtered = loanableItems.filter(t => {
    if (!matchesCatFilter(t)) return false;
    if (search && !t.navn.toLowerCase().includes(search.toLowerCase()) && !(t.beskrivelse || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (memberFilter && t.user_id !== memberFilter) return false;
    if (newFilter && !isRecent(t.created_at)) return false;
    return true;
  });

  const displayedTools = availableFilter
    ? filtered.filter(t => t.status === 'available')
    : filtered;

  const isSearchMode = showSearch && search.trim().length > 0;
  const listToShow: any[] = isSearchMode ? crossResults : displayedTools;

  const weeksFromNow = (weeks: number) => {
    const d = new Date();
    d.setDate(d.getDate() + weeks * 7);
    return d.toISOString().split('T')[0];
  };

  const openLoanPicker = () => {
    setLoanWeeks(2);
    setLoanDate(weeksFromNow(2));
    setShowPicker(true);
  };

  const handleRequest = async (itemId: string, date: string) => {
    setRequesting(itemId);
    setShowPicker(false);
    setRequestError(null);
    const err = await requestLoanItem(itemId, date || weeksFromNow(2));
    if (err) setRequestError(err);
    else setRequestedIds(s => new Set(s).add(itemId));
    setRequesting(null);
  };

  const chipBase: React.CSSProperties = {
    padding: '6px 12px', borderRadius: 30, fontSize: 13, fontWeight: 500,
    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
    cursor: 'pointer', flexShrink: 0,
  };

  if (groupsLoading) return <Spinner />;

  const firstName = profile?.full_name?.split(' ')[0] || '';
  const hour = new Date().getHours();
  const greeting = hour < 10 ? 'God morgen' : hour < 18 ? 'God ettermiddag' : 'God kveld';

  return (
    <div>
      {!isSearchMode && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{greeting}{firstName ? `, ${firstName}` : ''} 👋</div>
          </div>
          {members.length > 0 && (
            <button
              onClick={() => setShowMembersSheet(true)}
              style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 20, border: 'none', background: 'var(--green-light)', color: 'var(--green-dark)', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}
            >
              <Users size={12} strokeWidth={2} /> {members.length} medlemmer
            </button>
          )}
        </div>
      )}

      <DueSoonBanner items={myLoans} />

      {showSearch && (
        <div style={{ position: 'relative', marginBottom: 13 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', display: 'flex' }}><Search size={16} strokeWidth={1.8} /></span>
          <input autoFocus type="search" placeholder="Søk i alle grupper..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '11px 36px 11px 34px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, outline: 'none', background: 'var(--surface)', color: 'var(--text)' }} />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'var(--text3)', color: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
              <X size={12} strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      {/* Ledig nå + delings-banner side om side */}
      {!isSearchMode && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, height: 104 }}>
          <div
            onClick={() => setAvailableFilter(f => !f)}
            style={{
              flex: '0 0 108px',
              height: '100%',
              boxSizing: 'border-box',
              background: availableFilter ? 'var(--green-dark)' : 'var(--green)',
              borderRadius: 'var(--radius-sm)',
              padding: '14px 14px 12px',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
          >
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <Check size={13} color="#fff" strokeWidth={2.5} />
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{loanableItems.filter(t => t.status === 'available').length}</div>
            <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, marginTop: 3, lineHeight: 1.2 }}>Ledig nå</div>
          </div>

          {sponsors.length > 0 && sponsorConsent ? (
            <SponsorBanner sponsors={sponsors} />
          ) : (
            <div style={{ flex: 1, minWidth: 0, height: '100%', boxSizing: 'border-box', display: 'flex', gap: 10, alignItems: 'center', background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                🌍
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-dark)', lineHeight: 1.25 }}>Dere deler i stedet for å kjøpe nytt</div>
                <div style={{ fontSize: 11, color: 'var(--green-dark)', opacity: 0.85, marginTop: 2, lineHeight: 1.3 }}>
                  {loanableItems.length} ting deles i denne gruppen — det er {loanableItems.length} færre som må produseres på nytt.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter-rad */}
      <div style={{ display: 'flex', gap: 7, marginBottom: 13, alignItems: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any, paddingBottom: 2 }}>

        {(catFilter !== 'Alle' || memberFilter || availableFilter || newFilter) && (
          <button
            onClick={() => { setCatFilter('Alle'); setMemberFilter(''); setAvailableFilter(false); setNewFilter(false); setShowCatDropdown(false); setShowPersonDropdown(false); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, flexShrink: 0, borderRadius: '50%', border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text2)', cursor: 'pointer' }}
          >
            <X size={15} strokeWidth={2} />
          </button>
        )}

        <div ref={catBtnRef}>
          <button
            onClick={() => {
              const rect = catBtnRef.current?.getBoundingClientRect();
              if (rect) setCatDropPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 200) });
              setShowCatDropdown(s => !s); setShowPersonDropdown(false);
            }}
            style={{
              ...chipBase,
              border: `1px solid ${catFilter !== 'Alle' ? 'var(--green)' : 'var(--border-strong)'}`,
              background: catFilter !== 'Alle' ? 'var(--green)' : 'var(--surface)',
              color: catFilter !== 'Alle' ? '#fff' : 'var(--text2)',
            }}
          >
            {catFilter !== 'Alle'
              ? <>{categories.find(c => c.name === catFilter) && <CategoryIcon emoji={categories.find(c => c.name === catFilter)!.emoji} size={14} color="#fff" />} {catFilter}</>
              : 'Kategori'
            }
            <span style={{ fontSize: 10, opacity: 0.7 }}>{showCatDropdown ? ' ▴' : ' ▾'}</span>
          </button>
          {showCatDropdown && ReactDOM.createPortal(
            <>
              <div onClick={() => setShowCatDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
              <div style={{ position: 'fixed', top: catDropPos.top, left: catDropPos.left, zIndex: 200, background: 'var(--surface-solid)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', minWidth: 190, overflow: 'hidden' }}>
                {catOptions.map((c, i) => {
                  const cat = categories.find(x => x.name === c);
                  const active = catFilter === c;
                  return (
                    <button key={c} onClick={() => { setCatFilter(c); setShowCatDropdown(false); }} style={{
                      width: '100%', padding: '11px 14px',
                      border: 'none', borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                      background: active ? 'var(--green-light)' : 'var(--surface)',
                      color: active ? 'var(--green-dark)' : 'var(--text)',
                      fontSize: 14, fontWeight: active ? 600 : 400,
                      textAlign: 'left', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      {cat ? <CategoryIcon emoji={cat.emoji} size={15} color={active ? 'var(--green-dark)' : 'var(--text2)'} /> : <span style={{ width: 15 }} />}
                      <span style={{ flex: 1 }}>{c}</span>
                      {active && <span style={{ fontSize: 13 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body
          )}
        </div>

        <div ref={personBtnRef}>
          <button
            onClick={() => {
              const rect = personBtnRef.current?.getBoundingClientRect();
              if (rect) setPersonDropPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 200) });
              setShowPersonDropdown(s => !s); setShowCatDropdown(false);
            }}
            style={{
              ...chipBase,
              border: `1px solid ${memberFilter ? 'var(--green)' : 'var(--border-strong)'}`,
              background: memberFilter ? 'var(--green)' : 'var(--surface)',
              color: memberFilter ? '#fff' : 'var(--text2)',
            }}
          >
            {memberFilter && activeMember?.profile
              ? <><Avatar name={activeMember.profile.full_name} color={activeMember.profile.avatar_color} size="sm" />{memberFilter === user?.id ? 'Meg' : activeMember.profile.full_name.split(' ')[0]}</>
              : 'Person'
            }
            <span style={{ fontSize: 10, opacity: 0.7 }}>{showPersonDropdown ? ' ▴' : ' ▾'}</span>
          </button>
          {showPersonDropdown && ReactDOM.createPortal(
            <>
              <div onClick={() => setShowPersonDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
              <div style={{ position: 'fixed', top: personDropPos.top, left: personDropPos.left, zIndex: 200, background: 'var(--surface-solid)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', minWidth: 190, overflow: 'hidden' }}>
                <button onClick={() => { setMemberFilter(''); setShowPersonDropdown(false); }} style={{
                  width: '100%', padding: '11px 14px', border: 'none',
                  background: !memberFilter ? 'var(--green-light)' : 'var(--surface)',
                  color: !memberFilter ? 'var(--green-dark)' : 'var(--text)',
                  fontSize: 14, fontWeight: !memberFilter ? 600 : 400,
                  textAlign: 'left', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ flex: 1 }}>Alle</span>
                  {!memberFilter && <span style={{ fontSize: 13 }}>✓</span>}
                </button>
                {members.map(m => {
                  if (!m.profile) return null;
                  const active = memberFilter === m.user_id;
                  return (
                    <button key={m.id} onClick={() => { setMemberFilter(active ? '' : m.user_id); setShowPersonDropdown(false); }} style={{
                      width: '100%', padding: '11px 14px',
                      border: 'none', borderTop: '1px solid var(--border)',
                      background: active ? 'var(--green-light)' : 'var(--surface)',
                      color: active ? 'var(--green-dark)' : 'var(--text)',
                      fontSize: 14, fontWeight: active ? 600 : 400,
                      textAlign: 'left', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <Avatar name={m.profile!.full_name} color={m.profile.avatar_color} size="sm" />
                      <span style={{ flex: 1 }}>{m.user_id === user?.id ? 'Meg' : m.profile.full_name.split(' ')[0]}</span>
                      {active && <span style={{ fontSize: 13 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body
          )}
        </div>

        {hasNewItems && (
          <button
            onClick={() => setNewFilter(f => !f)}
            style={{
              ...chipBase,
              border: `1px solid ${newFilter ? 'var(--accent)' : 'var(--border-strong)'}`,
              background: newFilter ? 'var(--accent)' : 'var(--surface)',
              color: newFilter ? '#fff' : 'var(--text2)',
            }}
          >
            ✨ Nytt
          </button>
        )}

      </div>

      {requestError && (
        <div style={{ background: '#fee2e2', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--red)' }}>
          {requestError}
        </div>
      )}

      {isSearchMode && crossLoading && <Spinner />}

      {isSearchMode && !crossLoading && crossResults.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Ingen ting funnet</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Prøv et annet søkeord</div>
        </div>
      )}

      {isSearchMode && !crossLoading && crossResults.length > 0 && (
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
          {crossResults.length} treff på tvers av grupper
        </p>
      )}

      {!isSearchMode && displayedTools.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
            <span style={{ fontSize: 48 }}>{memberFilter ? '👤' : catOptions.length <= 1 ? '🧰' : '🔍'}</span>
          </div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {memberFilter ? 'Ingen ting funnet for dette medlemmet'
              : catOptions.length <= 1 ? 'Ingen lånbare ting i gruppen enda'
              : 'Ingen ting funnet'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            {catOptions.length <= 1 ? 'Gå til «Inventar» og aktiver «Kan lånes ut» for å vise ting her' : 'Prøv et annet søk eller filter'}
          </div>
        </div>
      )}

      {/* Kort-grid, 2 pr. rad */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {listToShow.map(item => {
          const isMe = item.user_id === user?.id;
          const loanedToMe = item.loaned_to_id === user?.id;
          const isPending = item.status === 'pending';
          const isOverdue = !!item.due_date && new Date(item.due_date) < new Date() && item.status === 'loaned';
          const dueDateLabel = item.due_date ? new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : null;

          let statusColor = 'var(--green)';
          let statusBg = 'var(--green-light)';
          let StatusIcon = Check;
          let statusLabel = 'Ledig';
          if (isPending) { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Clock; statusLabel = 'Venter'; }
          else if (item.status === 'loaned' && loanedToMe) { statusColor = 'var(--blue)'; statusBg = 'var(--blue-light)'; StatusIcon = Package; statusLabel = dueDateLabel ? `Lånt til ${dueDateLabel}` : 'Lånt'; }
          else if (item.status === 'loaned' && isOverdue) { statusColor = 'var(--red)'; statusBg = 'var(--red-light)'; StatusIcon = AlertTriangle; statusLabel = dueDateLabel ? `Forfalt ${dueDateLabel}` : 'Forfalt'; }
          else if (item.status === 'loaned') { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Package; statusLabel = dueDateLabel ? `Utlånt til ${dueDateLabel}` : 'Utlånt'; }
          const isNew = !isSearchMode && isRecent(item.created_at);

          return (
            <ImageTile
              key={item.id}
              imageUrl={item.bilde_url}
              emoji={item.emoji}
              name={item.navn}
              onClick={() => { setDetailItem(item); setShowPicker(false); }}
              onExpand={item.bilde_url ? () => setLightboxUrl(item.bilde_url) : undefined}
              statusColor={statusColor}
              statusBg={statusBg}
              statusLabel={statusLabel}
              StatusIcon={StatusIcon}
              ribbon={isNew ? 'Nytt' : undefined}
              topLeft={
                isSearchMode && (item.groupNames || []).length > 0 ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 20, background: 'var(--purple-light, #ede9fe)', color: 'var(--purple, #7c3aed)', fontSize: 11, fontWeight: 600 }}>
                    <Users size={9} strokeWidth={2} />{(item.groupNames as string[])[0]}
                  </span>
                ) : undefined
              }
              orgBadge={item.organization ? { name: item.organization.name } : undefined}
              groupBadge={!item.organization ? { names: item.groupNames || [] } : undefined}
            />
          );
        })}
      </div>

      {/* AdBanner midlertidig deaktivert: brøt scroll/klikk i appen mens AdSense-kontoen
          fortsatt venter på godkjenning. Skru på igjen når Google har godkjent kontoen. */}

      {/* Detaljbunnark */}
      {detailItem && (() => {
        const item = detailItem;
        const owner = item.owner;
        const loanedTo = item.loaned_to;
        const isMe = item.user_id === user?.id;
        const loanedToMe = item.loaned_to_id === user?.id;
        const isPending = item.status === 'pending';
        const isOverdue = !!item.due_date && new Date(item.due_date) < new Date() && item.status === 'loaned';
        const justRequested = requestedIds.has(item.id);

        return (
          <BottomSheet title={item.navn} onClose={() => { setDetailItem(null); setShowPicker(false); }}>
            {/* Bilde */}
            {item.bilde_url
              ? <div style={{ position: 'relative', marginBottom: 16 }} onClick={() => setLightboxUrl(item.bilde_url)}>
                  <img src={item.bilde_url} alt={item.navn}
                    style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block', cursor: 'zoom-in' }} />
                  <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '4px 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Maximize2 size={14} color="#fff" strokeWidth={2} />
                  </div>
                </div>
              : item.emoji && (
                  <div style={{ width: '100%', height: 110, borderRadius: 'var(--radius)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <CategoryIcon emoji={item.emoji} size={52} color="var(--green)" />
                  </div>
                )
            }

            {/* Eier + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              {item.organization ? (
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Building2 size={17} color="var(--green-dark)" strokeWidth={1.8} />
                </div>
              ) : (
                owner && <Avatar name={owner.full_name} color={owner.avatar_color} size="md" />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Eier</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{item.organization ? item.organization.name : (isMe ? 'Deg' : owner?.full_name)}</div>
              </div>
              <div>
                {item.status === 'available' && <Badge variant="green">Tilgjengelig</Badge>}
                {isPending && isMe && <Badge variant="amber">{loanedTo?.full_name?.split(' ')[0] ?? '?'} spør</Badge>}
                {isPending && loanedToMe && <Badge variant="amber">Venter på svar</Badge>}
                {isPending && !isMe && !loanedToMe && <Badge variant="amber">Forespurt</Badge>}
                {item.status === 'loaned' && loanedToMe && <Badge variant="blue">Du låner</Badge>}
                {item.status === 'loaned' && !loanedToMe && <Badge variant={isOverdue ? 'red' : 'amber'}>{isOverdue ? '⚠ Forfalt' : `Utlånt til ${loanedTo?.full_name?.split(' ')[0] ?? '?'}`}</Badge>}
              </div>
            </div>

            {/* Forfallsdato */}
            {item.due_date && item.status === 'loaned' && (
              <div style={{ fontSize: 13, color: isOverdue ? 'var(--red)' : 'var(--text3)', marginBottom: 12, padding: '8px 12px', background: isOverdue ? '#fee2e2' : 'var(--bg)', borderRadius: 'var(--radius-sm)' }}>
                {isOverdue ? '⚠ Forfalt: ' : '📅 Leveres: '}{new Date(item.due_date).toLocaleDateString('nb-NO')}
              </div>
            )}

            {/* Melding fra eier (låntaker) / fra låntaker (eier, etter retur) */}
            {loanedToMe && item.status === 'loaned' && item.approval_message && (
              <div style={{ fontSize: 13, color: 'var(--green-dark)', marginBottom: 12, padding: '8px 12px', background: 'var(--green-light)', borderRadius: 'var(--radius-sm)' }}>
                💬 {item.approval_message}
              </div>
            )}
            {isMe && item.status === 'available' && item.return_message && (
              <div style={{ fontSize: 13, color: 'var(--green-dark)', marginBottom: 12, padding: '8px 12px', background: 'var(--green-light)', borderRadius: 'var(--radius-sm)' }}>
                💬 {item.return_message}
              </div>
            )}

            {/* Beskrivelse */}
            {item.beskrivelse && (
              <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>{item.beskrivelse}</p>
            )}

            {/* Gruppe-badges (søk-modus) */}
            {isSearchMode && (item.groupNames || []).length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {(item.groupNames as string[]).map(n => (
                  <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: 'var(--purple-light, #ede9fe)', color: 'var(--purple, #7c3aed)', fontSize: 12, fontWeight: 600 }}>
                    <Users size={11} strokeWidth={2} />{n}
                  </span>
                ))}
              </div>
            )}

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 16px' }} />

            {/* Handlinger */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {isMe && isPending && (
                <>
                  <textarea
                    value={approveMessage}
                    onChange={e => setApproveMessage(e.target.value)}
                    placeholder="Legg til en melding til låntaker (valgfritt), f.eks. «Setter den bak garasjen»"
                    rows={2}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13.5, fontFamily: 'inherit', resize: 'none', marginBottom: 10, boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={async () => { await approveLoanItem(item.id, approveMessage); setApproveMessage(''); setDetailItem(null); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                      ✓ Godkjenn utlån
                    </button>
                    <button onClick={async () => { await rejectLoanItem(item.id); setApproveMessage(''); setDetailItem(null); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                      ✕ Avvis
                    </button>
                  </div>
                </>
              )}

              {loanedToMe && item.status === 'loaned' && (
                <>
                  <textarea
                    value={returnMessage}
                    onChange={e => setReturnMessage(e.target.value)}
                    placeholder="Legg til en melding til eieren (valgfritt), f.eks. «Satte den bak huset under presenningen»"
                    rows={2}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13.5, fontFamily: 'inherit', resize: 'none', marginBottom: 10, boxSizing: 'border-box' }}
                  />
                  <button onClick={async () => { await returnItem(item.id, returnMessage); setReturnMessage(''); setDetailItem(null); }} style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                    <Undo2 size={16} strokeWidth={2} /> Lever tilbake
                  </button>
                </>
              )}

              {isMe && !loanedToMe && item.status === 'loaned' && (
                <button onClick={async () => { await returnItem(item.id); fetchMyLentOut(user!.id); setDetailItem(null); }} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  ✓ Merk som tilbake
                </button>
              )}

              {!isMe && item.status === 'available' && !justRequested && (
                showPicker ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>Velg varighet</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[1,2,3,4].map(w => (
                        <button key={w} onClick={() => { setLoanWeeks(w); setLoanDate(weeksFromNow(w)); }}
                          style={{ flex: 1, padding: '9px 4px', borderRadius: 'var(--radius-sm)', border: `1px solid ${loanWeeks === w ? 'var(--green)' : 'var(--border-strong)'}`, background: loanWeeks === w ? 'var(--green)' : 'none', fontSize: 14, fontWeight: 600, color: loanWeeks === w ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                          {w}u
                        </button>
                      ))}
                    </div>
                    <input type="date" value={loanDate} min={new Date().toISOString().split('T')[0]}
                      onChange={e => { setLoanDate(e.target.value); setLoanWeeks(null); }}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'var(--surface)', color: 'var(--text)' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleRequest(item.id, loanDate)} disabled={requesting === item.id || !loanDate}
                        style={{ flex: 1, padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        {requesting === item.id ? '...' : <><Send size={16} strokeWidth={2} /> Send forespørsel</>}
                      </button>
                      <button onClick={() => setShowPicker(false)}
                        style={{ padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 14 }}>✕</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => openLoanPicker()} disabled={requesting === item.id}
                    style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {requesting === item.id ? '...' : <><HandHelping size={16} strokeWidth={2} /> Be om å låne</>}
                  </button>
                )
              )}

              {!isMe && justRequested && (
                <div style={{ textAlign: 'center', padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', color: 'var(--green-dark)', fontSize: 14, fontWeight: 600 }}>
                  ✓ Forespørsel sendt!
                </div>
              )}

              {isMe && (
                <button onClick={() => { setEditId(item.id); setDetailItem(null); }}
                  style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Pencil size={14} strokeWidth={2} /> Rediger
                </button>
              )}
            </div>
          </BottomSheet>
        );
      })()}

      {editId && (() => {
        const editItem = loanableItems.find(i => i.id === editId) || crossResults.find(i => i.id === editId);
        return editItem ? <EditItemSheet item={editItem} onClose={() => setEditId(null)} /> : null;
      })()}

      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      {showMembersSheet && (
        <BottomSheet title={`Medlemmer (${members.length})`} onClose={() => setShowMembersSheet(false)}>
          {members.map(m => m.profile && (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <Avatar name={m.profile.full_name} color={m.profile.avatar_color} size="sm" />
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                {m.user_id === user?.id ? `${m.profile.full_name} (deg)` : m.profile.full_name}
              </div>
              {m.role === 'admin' && (
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--green-dark)', background: 'var(--green-light)', padding: '2px 8px', borderRadius: 20 }}>Admin</span>
              )}
            </div>
          ))}
        </BottomSheet>
      )}
    </div>
  );
};
